import { Field } from '../../models/home';
import { useAuthProvider } from '../../providers/AuthProvider';
import { useMasterData } from '../../providers/MasterDataProvider';
import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import groupsJson from '../../constants/groups.json';
import { Dispatch, SetStateAction } from 'react';

interface Props {
    fields: Field[];
    setFields: Dispatch<SetStateAction<Field[]>>;
}

interface DropdownOption {
    text: string;
    value: string;
    id: string;
}

export const useCompensation = ({fields, setFields}: Props) => {
    const authContext = useAuthProvider();
    const { masterData } = useMasterData();
    const { i18n, t } = useTranslation();
    const language = i18n.language;

    const [showOptionalCommentsLabel, setShowOptionalCommentsLabel] = useState(true);
    const WIFI_REGIONS = ['', t('wifiRegion.na'), t('wifiRegion.sa'), t('wifiRegion.ta'), t('wifiRegion.tp')];

    const userGroupIds = useMemo(() => {
        if (!authContext.user?.groups?.length) return [];
        return authContext.user.groups.filter(groupId => {
            return (groupsJson as Array<{groupId: string, key: string}>).some(g => g.groupId === groupId);
        });
    }, [authContext.user?.groups]);

    const availableReasons = useMemo(() => {
        if (!masterData.reasons.length || !userGroupIds.length) return [];
        return masterData.reasons.filter(reason => {
            if (!reason.groupIds || !Array.isArray(reason.groupIds)) return false;
            return reason.groupIds.some(groupId => userGroupIds.includes(groupId));
        });
    }, [masterData.reasons, userGroupIds]);

    const reasonOptions: DropdownOption[] = useMemo(() => {
        if (!availableReasons.length) return [];
        return availableReasons.map(reason => {
            const fr = reason.reasonTitleFr || '';
            const en = reason.reasonTitleEn || '';
            const text = language === 'fr' ? (fr || en) : (en || fr);
            return {
                text,
                value: text,
                id: reason.reasonId
            };
        }).sort((a, b) => a.text.localeCompare(b.text));
    }, [availableReasons, language]);

    const selectedReasonId = useMemo(() => {
        const reasonField = fields.find(f => f.name === 'reasonId');
        return reasonField?.value || '';
    }, [fields]);

    const selectedReason = useMemo(() => {
        if (!selectedReasonId || !availableReasons.length) return null;
        return availableReasons.find(r => r.reasonId === selectedReasonId) || null;
    }, [selectedReasonId, availableReasons]);

    const selectedReasonDisplayText = useMemo(() => {
        if (!selectedReason) return '';
        const fr = selectedReason.reasonTitleFr || '';
        const en = selectedReason.reasonTitleEn || '';
        return language === 'fr' ? (fr || en) : (en || fr);
    }, [selectedReason, language]);

    const availableCategories = useMemo(() => {
        if (!selectedReasonId || !masterData.categories.length || !userGroupIds.length) return [];
        return masterData.categories.filter(category => {
            if (!category.reasonId || category.reasonId !== selectedReasonId) return false;
            if (!category.groupIds || !Array.isArray(category.groupIds)) return false;
            return category.groupIds.some(groupId => userGroupIds.includes(groupId));
        });
    }, [selectedReasonId, masterData.categories, userGroupIds]);

    const categoryOptions: DropdownOption[] = useMemo(() => {
        if (!availableCategories.length) return [];
        return availableCategories.map(category => {
            const fr = category.categoryTitleFr || '';
            const en = category.categoryTitleEn || '';
            const text = language === 'fr' ? (fr || en) : (en || fr);
            return {
                text,
                value: text,
                id: category.categoryId
            };
        }).sort((a, b) => a.text.localeCompare(b.text));
    }, [availableCategories, language]);

    const selectedCategoryId = useMemo(() => {
        const categoryField = fields.find(f => f.name === 'categoryId');
        return categoryField?.value || '';
    }, [fields]);

    const selectedCategory = useMemo(() => {
        if (!selectedCategoryId || !availableCategories.length) return null;
        return availableCategories.find(c => c.categoryId === selectedCategoryId) || null;
    }, [selectedCategoryId, availableCategories]);

    const selectedCategoryDisplayText = useMemo(() => {
        if (!selectedCategory) return '';
        const fr = selectedCategory.categoryTitleFr || '';
        const en = selectedCategory.categoryTitleEn || '';
        return language === 'fr' ? (fr || en) : (en || fr);
    }, [selectedCategory, language]);

    const cabinOptions: DropdownOption[] = useMemo(() => {
        // If current category has specific cabins, prefer full titles from master list using cabinId
        if (selectedCategory && Array.isArray(selectedCategory.cabins) && selectedCategory.cabins.length) {
            const opts = selectedCategory.cabins
                .map(c => {
                    const master = masterData.cabins.find(m => m.cabinId === c.cabinId);
                    let title = '';
                    if (master) {
                        // Use French title if available and language is French, otherwise fall back to English
                        if (language === 'fr') {
                            title = master.cabinTitleFr || master.cabinTitleEn || '';
                        } else {
                            title = master.cabinTitleEn || master.cabinTitleFr || '';
                        }
                    } else {
                        title = c.shorthandTxt || '';
                    }
                    return { 
                        text: title, 
                        value: title, 
                        id: c.cabinId 
                    };
                })
                .filter(opt => opt.text) // Filter out empty text options
                .sort((a, b) => a.text.localeCompare(b.text));
            return opts;
        }
        // Otherwise show all cabins from master list
        if (!masterData.cabins.length) return [];
        return masterData.cabins
            .map(cabin => {
                let text = '';
                if (language === 'fr') {
                    text = cabin.cabinTitleFr || cabin.cabinTitleEn || '';
                } else {
                    text = cabin.cabinTitleEn || cabin.cabinTitleFr || '';
                }
                return {
                    text,
                    value: text,
                    id: cabin.cabinId
                };
            })
            .filter(opt => opt.text) // Filter out empty text options
            .sort((a, b) => a.text.localeCompare(b.text));
    }, [selectedCategory, masterData.cabins, language]);

    const selectedCabinId = useMemo(() => {
        const cabinField = fields.find(f => f.name === 'cabinId');
        return cabinField?.value || '';
    }, [fields]);

    const selectedCabin = useMemo(() => {
        if (!selectedCabinId) return null;
        const fromMaster = masterData.cabins.find(c => c.cabinId === selectedCabinId);
        if (fromMaster) return fromMaster;
        // Fallback to category-specific cabin info if not in master list, but enrich from master when possible
        if (selectedCategory && Array.isArray(selectedCategory.cabins)) {
            const fromCategory = selectedCategory.cabins.find(c => c.cabinId === selectedCabinId);
            if (fromCategory) {
                const master = masterData.cabins.find(m => m.cabinId === fromCategory.cabinId);
                if (master) return master;
                return {
                    cabinId: fromCategory.cabinId,
                    cabinTitleEn: fromCategory.shorthandTxt ?? '',
                    cabinTitleFr: fromCategory.shorthandTxt ?? ''
                } as any;
            }
        }
        return null;
    }, [selectedCabinId, masterData.cabins, selectedCategory]);

    const selectedCabinDisplayText = useMemo(() => {
        // First check if we have a display value from the form field
        const cabinField = fields.find(f => f.name === 'cabinId');
        if (cabinField?.displayValue) {
            return cabinField.displayValue;
        }
        // Fall back to looking up from master data
        if (!selectedCabin) return '';
        return language === 'fr' ? selectedCabin.cabinTitleFr : selectedCabin.cabinTitleEn;
    }, [selectedCabin, language, fields]);

    const shouldShowCabin = useMemo(() => {
        if (selectedCategory && Array.isArray(selectedCategory.cabins)) {
            return selectedCategory.cabins.length > 0;
        }
        return false;
    }, [selectedCategory]);

    const compensationTypes = useMemo(() => {
        // 1) If category has cabin-specific compensation, require an explicit cabin selection
        if (selectedCategory && Array.isArray(selectedCategory.cabins) && selectedCategory.cabins.length) {
            if (selectedCabinId) {
                const matchedCabin = selectedCategory.cabins.find(c => c.cabinId === selectedCabinId);
                if (matchedCabin && matchedCabin.compensation) {
                    const cabinTypes = parseCompensationTypes(matchedCabin.compensation);
                    if (cabinTypes.length > 0) return cabinTypes;
                }
            }
            // No cabin selected or no mapping found: do not infer from first cabin
            return [];
        }
        // 2) Fallback to category-wide compensation
        if (selectedCategory && selectedCategory.compensation) {
            const categoryTypes = parseCompensationTypes(selectedCategory.compensation);
            if (categoryTypes.length > 0) return categoryTypes;
        }
        // 3) Fallback to reason-wide compensation
        if (selectedReason && selectedReason.compensation) {
            return parseCompensationTypes(selectedReason.compensation);
        }
        return [];
    }, [selectedCategory, selectedCabinId, selectedReason]);

    // Removed auto-select of cabin: user must pick a cabin when category has cabins

    function parseCompensationTypes(compensation: string | string[]): string[] {
        if (!compensation) return [];
        const types: string[] = [];
        let compensationArray: string[] = [];
        if (Array.isArray(compensation)) {
            compensationArray = compensation;
        } else if (typeof compensation === 'string') {
            try {
                const parsed = JSON.parse(compensation);
                if (Array.isArray(parsed)) {
                    compensationArray = parsed;
                } else {
                    compensationArray = [compensation];
                }
            } catch {
                compensationArray = [compensation];
            }
        } else {
            return [];
        }
        compensationArray.forEach(comp => {
            if (!comp || typeof comp !== 'string') return;
            const compUpper = comp.toUpperCase().trim();
            if (compUpper === 'WIFI' || compUpper.includes('WIFI')) {
                if (!types.includes(t('compensationTypes.wifi'))) {
                    types.push(t('compensationTypes.wifi'));
                }
            }
            if (compUpper === 'PERCENT_15' || compUpper.includes('PERCENT_15') || compUpper === '15' || compUpper.includes('PROMO15')) {
                if (!types.includes(t('compensationTypes.15'))) {
                    types.push(t('compensationTypes.15'));
                }
            }
            if (compUpper === 'PERCENT_20' || compUpper.includes('PERCENT_20') || compUpper === '20' || compUpper.includes('PROMO20')) {
                if (!types.includes(t('compensationTypes.20'))) {
                    types.push(t('compensationTypes.20'));
                }
            }
            if (compUpper === 'PERCENT_25' || compUpper.includes('PERCENT_25') || compUpper === '25' || compUpper.includes('PROMO25')) {
                if (!types.includes(t('compensationTypes.25'))) {
                    types.push(t('compensationTypes.25'));
                }
            }
            if (compUpper === 'PERCENT_30' || compUpper.includes('PERCENT_30') || compUpper === '30' || compUpper.includes('PROMO30')) {
                if (!types.includes(t('compensationTypes.30'))) {
                    types.push(t('compensationTypes.30'));
                }
            }
        });
        return types;
    }

    useEffect(() => {
        if (!selectedReasonId && selectedCategoryId) {
            setFields(prevFields => {
                return prevFields.map(f => {
                    if (f.name === 'categoryId') {
                        return { ...f, value: '' };
                    }
                    if (f.name === 'cabinId') {
                        return { ...f, value: '' };
                    }
                    if (f.name === 'compensationType') {
                        return { ...f, value: '' };
                    }
                    return f;
                });
            });
        }
    }, [selectedReasonId, selectedCategoryId, setFields]);

    useEffect(() => {
        if (!selectedCategoryId && selectedCabinId) {
            setFields(prevFields => {
                return prevFields.map(f => {
                    if (f.name === 'cabinId') {
                        return { ...f, value: '' };
                    }
                    if (f.name === 'compensationType') {
                        return { ...f, value: '' };
                    }
                    return f;
                });
            });
        }
    }, [selectedCategoryId, selectedCabinId, setFields]);

    useEffect(() => {
        if (selectedCategoryId && !shouldShowCabin && selectedCabinId) {
            setFields(prevFields => {
                return prevFields.map(f => {
                    if (f.name === 'cabinId') {
                        return { ...f, value: '' };
                    }
                    return f;
                });
            });
        }
    }, [selectedCategoryId, shouldShowCabin, selectedCabinId, setFields]);

    // Clear WiFi region when compensation type changes to non-WiFi
    useEffect(() => {
        const compensationTypeField = fields.find(f => f.name === 'compensationType');
        const wifiRegionField = fields.find(f => f.name === 'wifiRegion');
        
        if (compensationTypeField && wifiRegionField) {
            const isWifiSelected = compensationTypeField.value === t('compensationTypes.wifi');
            
            if (!isWifiSelected && wifiRegionField.value) {
                setFields(prevFields => 
                    prevFields.map(f => 
                        f.name === 'wifiRegion' 
                            ? { ...f, value: '' } 
                            : f
                    )
                );
            }
        }
    }, [fields, t]);

    useEffect(() => {
        const categoryField = fields.find(f => f.name === 'categoryId');
        if (categoryField && categoryField.value) {
            const categoryExists = availableCategories.some(c => c.categoryId === categoryField.value);
            if (!categoryExists) {
                setFields(prevFields => {
                    return prevFields.map(f => {
                        if (f.name === 'categoryId') {
                            return { ...f, value: '' };
                        }
                        if (f.name === 'cabinId') {
                            return { ...f, value: '' };
                        }
                        if (f.name === 'compensationType') {
                            return { ...f, value: '' };
                        }
                        return f;
                    });
                });
            }
        }
    }, [availableCategories, fields, setFields]);

    useEffect(() => {
        const reasonField = fields.find(f => f.name === 'reasonId');
        if (reasonField && reasonField.value) {
            const reasonExists = availableReasons.some(r => r.reasonId === reasonField.value);
            if (!reasonExists) {
                setFields(prevFields => {
                    return prevFields.map(f => {
                        if (f.name === 'reasonId') {
                            return { ...f, value: '' };
                        }
                        if (f.name === 'categoryId') {
                            return { ...f, value: '' };
                        }
                        if (f.name === 'cabinId') {
                            return { ...f, value: '' };
                        }
                        if (f.name === 'compensationType') {
                            return { ...f, value: '' };
                        }
                        return f;
                    });
                });
            }
        }
    }, [availableReasons, fields, setFields]);

    return {
        reasonOptions,
        categoryOptions,
        cabinOptions,
        compensationTypes,
        selectedReasonId,
        selectedCategoryId,
        selectedCabinId,
        selectedReason,
        selectedCategory,
        selectedCabin,
        selectedReasonDisplayText,
        selectedCategoryDisplayText,
        selectedCabinDisplayText,
        shouldShowCabin,
        showOptionalCommentsLabel,
        WIFI_REGIONS,
        setShowOptionalCommentsLabel,
        t
    };
};
